import type { NextApiRequest, NextApiResponse } from 'next';
import { ASSISTANT_TOOLS, executeToolCall } from '@/lib/ai-tools';

export const config = {
  api: {
    bodyParser: { sizeLimit: '1mb' },
    responseLimit: false,
  },
};

interface AsesorChatRequest {
  asesorId: string;
  conversacionId: string;
  mensaje: string;
  historial?: { rol: string; contenido: string }[];
}

interface ToolCallInfo {
  name: string;
  args: Record<string, unknown>;
  result: { success: boolean; data?: unknown; error?: string };
}

interface OpenAIDelta {
  content?: string | null;
  tool_calls?: Array<{
    index: number;
    id?: string;
    type?: string;
    function?: {
      name?: string;
      arguments?: string;
    };
  }>;
}

interface OpenAIStreamChoice {
  index: number;
  delta: OpenAIDelta;
  finish_reason: string | null;
}

const MAX_TOOL_ITERATIONS = 5;

const SYSTEM_PROMPT = `Eres E2, un asistente inteligente especializado en contabilidad, fiscalidad y gestión empresarial para asesores fiscales en España.

CAPACIDADES CON HERRAMIENTAS:
Tienes acceso a herramientas del sistema que te permiten realizar acciones REALES:
- Listar y buscar clientes asignados
- Listar, buscar y CREAR facturas
- Buscar documentos subidos y procesarlos con OCR
- Consultar resúmenes financieros de clientes
- Listar y crear incidencias

REGLAS CRÍTICAS:
- SIEMPRE responde en español
- Cuando el usuario pida datos concretos (clientes, facturas, documentos), USA las herramientas — NO inventes datos
- Para crear facturas o incidencias, confirma los datos con el usuario antes de ejecutar la herramienta
- Cuando uses una herramienta y obtengas resultados, preséntalos de forma clara y organizada con markdown
- Si una herramienta falla, explica el error al usuario y sugiere alternativas
- Usa lenguaje técnico pero claro
- Estructura tus respuestas con headers, bullets o tablas cuando sea apropiado
- Cita la normativa aplicable cuando sea relevante
- Si no estás seguro de algo, indícalo claramente`;

/**
 * Send an SSE event to the client.
 */
function sendEvent(res: NextApiResponse, event: string, data: unknown): void {
  res.write(`event: ${event}\ndata: ${JSON.stringify(data)}\n\n`);
}

/**
 * Parse a single SSE line from OpenAI's streaming response.
 * Returns the parsed JSON object or null for non-data lines / [DONE].
 */
function parseSSELine(line: string): { choices: OpenAIStreamChoice[] } | null {
  const trimmed = line.trim();
  if (!trimmed.startsWith('data: ')) return null;
  const payload = trimmed.slice(6);
  if (payload === '[DONE]') return null;
  try {
    return JSON.parse(payload) as { choices: OpenAIStreamChoice[] };
  } catch {
    return null;
  }
}

/**
 * Streaming Asesor Assistant Chat API with OpenAI + Function Calling (SSE)
 */
export default async function handler(
  req: NextApiRequest,
  res: NextApiResponse
) {
  if (req.method !== 'POST') {
    res.status(405).json({ success: false, error: 'Method not allowed' });
    return;
  }

  // SSE headers
  res.writeHead(200, {
    'Content-Type': 'text/event-stream',
    'Cache-Control': 'no-cache, no-transform',
    Connection: 'keep-alive',
    'X-Accel-Buffering': 'no',
  });

  // Client disconnect flag
  let aborted = false;
  req.on('close', () => {
    aborted = true;
  });

  try {
    const { asesorId, conversacionId, mensaje, historial } = req.body as AsesorChatRequest;

    if (!asesorId || !conversacionId || !mensaje) {
      sendEvent(res, 'error', { error: 'Missing required fields' });
      res.end();
      return;
    }

    const openaiApiKey = process.env.OPENAI_API_KEY;

    if (!openaiApiKey) {
      sendEvent(res, 'error', { error: 'OpenAI API key not configured' });
      res.end();
      return;
    }

    // Build conversation context
    const messages: Array<{
      role: string;
      content?: string | null;
      tool_calls?: unknown[];
      tool_call_id?: string;
    }> = [{ role: 'system', content: SYSTEM_PROMPT }];

    if (historial && historial.length > 0) {
      historial.slice(-8).forEach((msg) => {
        messages.push({
          role: msg.rol === 'usuario' ? 'user' : 'assistant',
          content: msg.contenido,
        });
      });
    }

    messages.push({ role: 'user', content: mensaje });

    const toolContext = { userId: asesorId, userRole: 'asesor' };
    const allToolCalls: ToolCallInfo[] = [];

    // ─── Streaming Function Calling Loop ───
    for (let iteration = 0; iteration < MAX_TOOL_ITERATIONS; iteration++) {
      if (aborted) break;

      const openaiResponse = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${openaiApiKey}`,
        },
        body: JSON.stringify({
          model: 'gpt-4o',
          messages,
          tools: ASSISTANT_TOOLS,
          tool_choice: 'auto',
          temperature: 0.7,
          max_tokens: 3000,
          stream: true,
        }),
      });

      if (!openaiResponse.ok) {
        const errorText = await openaiResponse.text();
        console.error('OpenAI API error:', errorText);
        sendEvent(res, 'error', { error: 'Error al comunicarse con OpenAI' });
        res.end();
        return;
      }

      const body = openaiResponse.body;
      if (!body) {
        sendEvent(res, 'error', { error: 'Empty response body from OpenAI' });
        res.end();
        return;
      }

      // Accumulate content and tool_calls from streaming chunks
      let contentAccumulated = '';
      let finishReason: string | null = null;

      // Tool calls accumulator: keyed by index
      const pendingToolCalls: Map<
        number,
        { id: string; name: string; arguments: string }
      > = new Map();

      // Read the stream
      const reader = body.getReader();
      const decoder = new TextDecoder('utf-8');
      let sseBuffer = '';

      let streamDone = false;
      while (!streamDone && !aborted) {
        const { value, done } = await reader.read();
        if (done) {
          streamDone = true;
          break;
        }

        sseBuffer += decoder.decode(value, { stream: true });

        // Process complete lines
        const lines = sseBuffer.split('\n');
        // Keep the last potentially incomplete line in the buffer
        sseBuffer = lines.pop() ?? '';

        for (const line of lines) {
          const parsed = parseSSELine(line);
          if (!parsed) continue;

          const choice = parsed.choices[0];
          if (!choice) continue;

          const delta = choice.delta;

          // Stream text content tokens to client
          if (delta.content) {
            contentAccumulated += delta.content;
            sendEvent(res, 'delta', { content: delta.content });
          }

          // Accumulate tool_calls fragments
          if (delta.tool_calls) {
            for (const tc of delta.tool_calls) {
              const idx = tc.index;
              let entry = pendingToolCalls.get(idx);
              if (!entry) {
                entry = { id: '', name: '', arguments: '' };
                pendingToolCalls.set(idx, entry);
              }
              if (tc.id) entry.id = tc.id;
              if (tc.function?.name) entry.name += tc.function.name;
              if (tc.function?.arguments) entry.arguments += tc.function.arguments;
            }
          }

          if (choice.finish_reason) {
            finishReason = choice.finish_reason;
          }
        }
      }

      // Process any remaining data in the buffer
      if (sseBuffer.trim()) {
        const parsed = parseSSELine(sseBuffer);
        if (parsed) {
          const choice = parsed.choices[0];
          if (choice) {
            if (choice.delta.content) {
              contentAccumulated += choice.delta.content;
              sendEvent(res, 'delta', { content: choice.delta.content });
            }
            if (choice.finish_reason) {
              finishReason = choice.finish_reason;
            }
          }
        }
      }

      if (aborted) break;

      // Handle tool calls
      if (finishReason === 'tool_calls' && pendingToolCalls.size > 0) {
        // Build the assistant message with tool_calls for message history
        const toolCallsForHistory = Array.from(pendingToolCalls.entries())
          .sort(([a], [b]) => a - b)
          .map(([, tc]) => ({
            id: tc.id,
            type: 'function' as const,
            function: { name: tc.name, arguments: tc.arguments },
          }));

        messages.push({
          role: 'assistant',
          content: contentAccumulated || null,
          tool_calls: toolCallsForHistory,
        });

        // Execute each tool call
        for (const [, tc] of pendingToolCalls) {
          let fnArgs: Record<string, unknown> = {};
          try {
            fnArgs = JSON.parse(tc.arguments) as Record<string, unknown>;
          } catch {
            fnArgs = {};
          }

          sendEvent(res, 'tool_start', { name: tc.name, args: fnArgs });

          console.log(`[AI Tool Stream] Executing: ${tc.name}`, fnArgs);
          const result = await executeToolCall(tc.name, fnArgs, toolContext);

          allToolCalls.push({ name: tc.name, args: fnArgs, result });

          sendEvent(res, 'tool_result', { name: tc.name, args: fnArgs, result });

          // Add tool result to message history
          messages.push({
            role: 'tool',
            tool_call_id: tc.id,
            content: JSON.stringify(result),
          });
        }

        // Continue the loop — next iteration will process tool results
        continue;
      }

      // Model returned a final text response — we're done
      sendEvent(res, 'done', {
        toolCalls: allToolCalls.length > 0 ? allToolCalls : undefined,
      });
      break;
    }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Error interno del servidor';
    console.error('Asesor streaming chat error:', message);
    if (!aborted) {
      sendEvent(res, 'error', { error: message });
    }
  } finally {
    if (!aborted) {
      res.end();
    }
  }
}
